import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Theme architecture: each theme id has a matching `[data-theme="id"]` block in globals.css
// (the default "haier" palette lives in :root). ThemeProvider sets <html data-theme="id">.
// To add a theme later: add its id here + a CSS block. A per-dealer preference (from
// tenant_settings) can feed `initialTheme` in the future — no refactor needed.
const STORAGE_KEY = 'pos-theme';
const DEFAULT_THEME = 'haier';

export const THEMES = ['haier'] as const;
export type ThemeId = (typeof THEMES)[number];

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (t: ThemeId) => void;
  themes: readonly ThemeId[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function resolveInitial(): ThemeId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (THEMES as readonly string[]).includes(stored)) return stored as ThemeId;
  } catch {
    /* ignore */
  }
  return DEFAULT_THEME;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(resolveInitial);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const setTheme = useCallback((t: ThemeId) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
